import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Warehouse as WarehouseIcon, DollarSign, ScrollText, Users , Search, X } from 'lucide-react';
import client from '@/lib/api';
import PageHeader from '@/components/PageHeader';
import { CardSkeleton } from '@/components/Skeletons';
import { formatCurrency, formatNumber } from '@/lib/format';
import { Link } from 'react-router-dom';

export default function AdminOverview() {
  const whQ = useQuery({
    queryKey: ['admin', 'warehouses-summary'],
    queryFn: () => client.get('/warehouses', { params: { limit: 100 } }).then((r) => r.data),
  });
  const costQ = useQuery({
    queryKey: ['admin', 'cost-per-customer'],
    queryFn: () => client.get('/platform-billing/cost-per-customer').then((r) => r.data),
  });
  const invQ = useQuery({
    queryKey: ['admin', 'platform-invoices-summary'],
    queryFn: () => client.get('/platform-billing/invoices', { params: { limit: 5 } }).then((r) => r.data),
  });

  const warehouses = whQ.data?.data || [];
  const activeWarehouses = warehouses.filter((w) => w.status === 'ACTIVE').length;
  const totalCustomers = warehouses.reduce((s, w) => s + (w.customerCount || 0), 0);
  const cost = costQ.data?.data?.costPerCustomer || 0;
  const unpaidTotal = (invQ.data?.data || []).filter((i) => i.status === 'UNPAID').reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div data-testid="admin-overview-page">
      <PageHeader
        title="Platform overview"
        description="Snapshot of your warehouses, customers, and platform billing."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={WarehouseIcon} label="Warehouses" value={warehouses.length} sub={`${activeWarehouses} active`} loading={whQ.isLoading} testId="kpi-warehouses" />
        <Kpi icon={Users} label="Total customers" value={formatNumber(totalCustomers)} sub="Across all warehouses" loading={whQ.isLoading} testId="kpi-customers" />
        <Kpi icon={DollarSign} label="Cost per customer" value={formatCurrency(cost)} sub="Global rate" loading={costQ.isLoading} testId="kpi-cost" />
        <Kpi icon={ScrollText} label="Unpaid platform invoices" value={formatCurrency(unpaidTotal)} sub={`${(invQ.data?.data || []).filter((i) => i.status === 'UNPAID').length} invoice(s)`} loading={invQ.isLoading} testId="kpi-unpaid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          {/* Search and Customer Dropdown Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by Invoice No, Customer, Stock ID, Batch..."
            value={invoiceSearchTerm}
            onChange={(e) => setInvoiceSearchTerm(e.target.value)}
            className="w-full h-9 pl-9 pr-8 rounded-lg border border-zinc-200 bg-white text-xs font-mono text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 placeholder:text-zinc-400 font-sans"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-zinc-400">
            <Search className="w-3.5 h-3.5" />
          </div>
          {invoiceSearchTerm && (
            <button
              onClick={() => setInvoiceSearchTerm("")}
              className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-zinc-400 hover:text-zinc-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="w-full h-9 px-3 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
          >
            <option value="">All Customers</option>
            {customerOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <div>
              <h3 className="font-display font-semibold text-zinc-950">Recent warehouses</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Newest platform tenants</p>
            </div>
            <Link to="/admin/warehouses" className="text-xs text-zinc-950 hover:underline font-medium">View all →</Link>
          </div>
          <div className="divide-y divide-zinc-100" data-testid="overview-recent-warehouses">
            {whQ.isLoading ? (
              <div className="p-4 space-y-2"><CardSkeleton height={40} /><CardSkeleton height={40} /><CardSkeleton height={40} /></div>
            ) : warehouses.slice(0, 5).length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">No warehouses yet. Create one to get started.</div>
            ) : warehouses.slice(0, 5).map((w) => (
              <div key={w.id} className="px-5 py-3 flex items-center justify-between hover:bg-zinc-50 transition-colors">
                <div>
                  <div className="text-sm font-medium text-zinc-950">{w.name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{w.email}</div>
                </div>
                <div className="text-xs font-mono text-zinc-500">{w.customerCount ?? 0} customer(s)</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <div>
              <h3 className="font-display font-semibold text-zinc-950">Recent platform invoices</h3>
              <p className="text-xs text-zinc-500 mt-0.5">Warehouse subscription billing</p>
            </div>
            <Link to="/admin/billing" className="text-xs text-zinc-950 hover:underline font-medium">View all →</Link>
          </div>
          <div className="divide-y divide-zinc-100" data-testid="overview-recent-invoices">
            {invQ.isLoading ? (
              <div className="p-4 space-y-2"><CardSkeleton height={40} /><CardSkeleton height={40} /><CardSkeleton height={40} /></div>
            ) : (invQ.data?.data || []).length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">No invoices yet.</div>
            ) : (invQ.data?.data || []).slice(0, 5).map((i) => (
              <div key={i.id} className="px-5 py-3 flex items-center justify-between hover:bg-zinc-50 transition-colors">
                <div>
                  <div className="text-sm font-mono text-zinc-950">{i.invoiceNumber}</div>
                  <div className="text-xs text-zinc-500">{i.warehouse?.name} · {i.billingMonth}/{i.billingYear}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-mono font-bold text-zinc-950">{formatCurrency(i.amount)}</div>
                  <div className={`text-[10px] font-mono uppercase tracking-widest mt-0.5 ${i.status === 'PAID' ? 'text-emerald-700' : 'text-zinc-500'}`}>{i.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, loading, testId }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 relative overflow-hidden hover:border-zinc-300 transition-colors" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-9 h-9 rounded-md bg-zinc-100 grid place-items-center">
          <Icon className="w-4 h-4 text-zinc-700" strokeWidth={1.75} />
        </div>
      </div>
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      {loading ? (
        <div className="h-8 w-24 rounded shimmer" />
      ) : (
        <div className="font-display text-2xl font-bold text-zinc-950 tabular-nums">{value}</div>
      )}
      {sub && <div className="text-xs text-zinc-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
}
